import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import notice from '../../../assets/icons/backOffice/Earnings/notice.svg';
import usdtIcon from '../../../assets/icons/backOffice/USDT.svg';
import { InputBackOffice } from '../../../components/common/InputBackoffice';
import { ErrorToast } from '../../../components/common/Toasts/Toasts';
import {
  USDC,
  USDT,
  formatUSD,
  tokens
} from '../../../helpers/helperFunctions';
import { P3 } from '../../../styles/FontStyles';

import { SelectNetwork } from '../../../components';
import { Button } from '../../../styles/GlobalComponents';
import { color } from '../../../styles/GlobalStyles';
import {
  Icon,
  IconFlipMeWallet,
  IconWithdrawWallet
} from '../../../styles/Icons';
import {
  BankCard,
  FlexBetween,
  FlexCenter
} from '../../../styles/stylesStyled';
import { SmallTxt, Subtitle, Title } from './WithdrawStyled';
import { SelectToken } from '../../../components/common/SelectNetwork/SelectToken';
import { WalletActivation } from './Withdraw.types';

interface WithdrawInitialProps {
  setStep: Dispatch<SetStateAction<number>>;
  userWallets: any;
  amount: string;
  setAmount: Dispatch<SetStateAction<string>>;
  selected: any;
  info: any;
  selectedNetwork: any;
  setSelectedNetwork: any;
  selectedToken: any;
  setSelectedToken: any;
  isWithdrawSelected: boolean;
  setIsWithdrawSelected: any;
  selectedWallet: any;
}

export const WithdrawInitial = ({
  setStep,
  userWallets,
  amount,
  setAmount,
  selected,
  info,
  selectedNetwork,
  setSelectedNetwork,
  selectedToken,
  setSelectedToken,
  isWithdrawSelected,
  setIsWithdrawSelected,
  selectedWallet
}: WithdrawInitialProps) => {
  const { t } = useTranslation('earnings');
  const { t: network } = useTranslation('marketplace');

  const digit = /^(?=.*?[0-9])/;

  const showWalletButton = () => {
    if (selectedWallet) {
      if (selectedWallet?.status == WalletActivation.INACTIVE) {
        return t('withdraw_part.initial.add');
      } else {
        return t('withdraw_part.initial.change');
      }
    } else {
      return t('withdraw_part.initial.add');
    }
  };

  const showSteps = () => {
    if (selectedWallet) {
      if (selectedWallet?.status == WalletActivation.INACTIVE) {
        setStep(5);
      } else {
        setStep(4);
      }
    } else {
      return setStep(5);
    }
  };

  const showWallet = () => {
    if (selectedWallet) {
      if (selectedWallet.status == WalletActivation.INACTIVE) {
        return (
          <div style={{ display: 'flex' }}>
            <img src={notice} alt='card' />
            {t('withdraw_part.initial.add_address_p1')}

            <br />
            {t('withdraw_part.initial.add_address_p2', {
              USDT: USDT
            })}
          </div>
        );
      } else {
        return '';
      }
    } else {
      return (
        <div style={{ display: 'flex' }}>
          <img src={notice} alt='card' />
          {t('withdraw_part.initial.add_address_p1')}

          <br />
          {t('withdraw_part.initial.add_address_p2', {
            USDT: USDT
          })}
        </div>
      );
    }
  };

  return (
    <div style={{ marginTop: '8vh' }}>
      <FlexCenter style={{ flexDirection: 'column' }}>
        <Title>{t('withdraw_part.initial.title')}</Title>
        <Subtitle style={{ textAlign: 'center' }}>
          Please make sure that the wallet you are adding is capable of
          receiving USDT or USDC.
        </Subtitle>
        <InputBackOffice
          placeholder={t('withdraw_part.initial.witdhrawal_amount_placeholder')}
          style={{ marginBottom: 8 }}
          onChange={(e: string) => {
            setAmount(e.replace(/[.,\s\D]/g, ''));
          }}
          pattern='[0-9]'
          value={amount.replace(/[.,\s\D]/g, '')}
        />
        <FlexBetween style={{ width: '100%', marginBottom: 12 }}>
          <SmallTxt>
            {t('withdraw_part.initial.balance')}:{' '}
            {formatUSD(info?.totalEarnings?.availableBalance)}{' '}
            {selectedToken?.token === USDC ? USDC : USDT}
          </SmallTxt>
          <SmallTxt>{`Withdrawal fee is 5 ${
            selectedToken?.token === USDC ? USDC : USDT
          }`}</SmallTxt>
        </FlexBetween>
        <FlexBetween style={{ width: '100%', marginBottom: 12 }}>
          <P3 bold mb='8' mt='10' color={color.colors.neutral900}>
            {network('select_network')}
          </P3>
        </FlexBetween>
        <SelectNetwork
          onChange={(n) => {
            if (n.networkType === 2) {
              setIsWithdrawSelected(true);
              setSelectedToken(tokens[0]);
            } else {
              setIsWithdrawSelected(false);
            }
            setSelectedNetwork(n);
          }}
          value={selectedNetwork}
        />

        <FlexBetween style={{ width: '100%', marginBottom: 10 }}>
          <P3 bold mb='8' mt='10' color={color.colors.neutral900}>
            {network('select_token')}
          </P3>
        </FlexBetween>

        <SelectToken
          isWithdrawSelected={isWithdrawSelected}
          onChange={(token) => {
            setSelectedToken(token);
          }}
          value={selectedToken}
        />

        <BankCard>
          <div>
            <div>{showWallet()}</div>
            {selectedWallet?.status === WalletActivation.ACTIVE && (
              <FlexCenter>
                {userWallets?.length > 0 && (
                  <Icon mr='10' color={color.colors.neutral900}>
                    {selectedWallet?.isFlipme ? (
                      <IconFlipMeWallet />
                    ) : (
                      <IconWithdrawWallet />
                    )}
                  </Icon>
                )}

                <P3
                  color={color.colors.neutral900}
                  bold
                  style={{ wordBreak: 'break-all' }}>
                  {userWallets?.length > 0 && selectedWallet?.address}
                </P3>
              </FlexCenter>
            )}
          </div>
          <Button ml='10' w='auto' h='36px' onClick={() => showSteps()}>
            <span>{showWalletButton()}</span>
          </Button>
        </BankCard>

        <Button
          maxWidth='200px'
          disabled={!selectedNetwork || !amount || !selectedToken}
          onClick={() => {
            if (!amount) {
              ErrorToast(t('withdraw_part.initial.err_amount_required'));
            } else if (userWallets?.length === 0) {
              ErrorToast(t('withdraw_part.initial.err_address_required'));
            } else if (!amount.match(digit)) {
              ErrorToast(t('withdraw_part.initial.err_number_required'));
            } else if (amount > info?.totalEarnings?.availableBalance) {
              ErrorToast(
                t('withdraw_part.initial.err_no_enough_usdt', { wULX: USDT })
              );
            } else if (!selectedNetwork) {
              ErrorToast(t('withdraw_part.initial.err_no_network'));
            } else {
              setStep(2);
            }
          }}>
          {t('withdraw_part.initial.withdraw')}
        </Button>
      </FlexCenter>
    </div>
  );
};
